import { appEnv } from '@/lib/env';

export interface ApiErrorBody {
  message?: string;
  code?: string;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: ApiErrorBody | null;

  constructor(status: number, body: ApiErrorBody | null, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export class RedirectError extends Error {
  public readonly url: string;
  constructor(url: string) {
    super('Redirected');
    this.name = 'RedirectError';
    this.url = url;
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/**
 * API 呼び出しの薄いラッパ。
 * - ベース URL は appEnv 経由 (ビルド時に VITE_API_BASE_URL から解決)
 * - JSON 前提
 * - エラーは ApiError に正規化する
 */
export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, signal, headers } = options;

  const url = joinUrl(appEnv.apiBaseUrl, path);
  const response = await fetch(url, {
    method,
    signal,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Intercept 302 Redirects that fetch transparently followed
  if (response.redirected && response.url) {
    throw new RedirectError(response.url);
  }

  if (!response.ok) {
    const errorBody = await safeParseJson<ApiErrorBody>(response);
    throw new ApiError(
      response.status,
      errorBody,
      errorBody?.message ?? `API request failed: ${response.status}`,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

function joinUrl(base: string, path: string): string {
  const trimmedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const trimmedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${trimmedPath}`;
}

async function safeParseJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}
